import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "flowbite-react";
import { HiExternalLink } from "react-icons/hi";
import { auctionPropertiesThunks } from "../../app/store";
import { propertyLiensApi } from "../../api/propertyLiensApi";
import { parseMoneyToNumber } from "../../utils/numberParser";
import LienEditorCard from "./liens/LienEditorCard";
import LienFormFields from "./liens/LienFormFields";
import LiensSummaryCards from "./liens/LiensSummaryCards";

const EMPTY_LIEN_FORM = {
  lien_type: "",
  amount: "",
  recorded_at: "",
  status: "UNKNOWN",
  source_url: "",
  notes: "",
};

function toMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "$0.00";

  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function toAmountNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function buildEditBuffer(liens) {
  const next = {};

  for (const lien of liens) {
    next[lien.id] = {
      lien_type: lien.lien_type || "",
      amount: lien.amount ?? "",
      recorded_at: lien.recorded_at || "",
      status: lien.status || "UNKNOWN",
      source_url: lien.source_url || "",
      notes: lien.notes || "",
    };
  }

  return next;
}

export default function Step6Liens({
  propertyId,
  property,
  onSave,
  publicRecordsLink,
  publicRecordsLinks = [],
}) {
  const dispatch = useDispatch();

  const liensFromProperty = useMemo(() => {
    const all = property?.liens || property?.PropertyLiens || [];
    return [...all].sort((a, b) => {
      const dateA = new Date(a.recorded_at || a.recordedAt || "1970-01-01");
      const dateB = new Date(b.recorded_at || b.recordedAt || "1970-01-01");
      return dateB - dateA;
    });
  }, [property]);

  const summary = useMemo(() => {
    const open = liensFromProperty.filter((lien) => lien.status === "OPEN");
    const unknown = liensFromProperty.filter((lien) => lien.status === "UNKNOWN");
    const released = liensFromProperty.filter((lien) => lien.status === "RELEASED");

    const openAmount = open.reduce(
      (total, lien) => total + toAmountNumber(lien.amount),
      0,
    );
    const unknownAmount = unknown.reduce(
      (total, lien) => total + toAmountNumber(lien.amount),
      0,
    );

    return {
      total: liensFromProperty.length,
      openCount: open.length,
      unknownCount: unknown.length,
      releasedCount: released.length,
      openPlusUnknownAmount: openAmount + unknownAmount,
    };
  }, [liensFromProperty]);

  const [createForm, setCreateForm] = useState(EMPTY_LIEN_FORM);
  const [editMap, setEditMap] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setEditMap(buildEditBuffer(liensFromProperty));
  }, [liensFromProperty]);

  function setEdit(id, patch) {
    setEditMap((current) => ({
      ...current,
      [id]: { ...(current[id] || {}), ...patch },
    }));
  }

  async function refreshProperty() {
    if (onSave) return onSave();
    if (propertyId) {
      return dispatch(auctionPropertiesThunks.getById(propertyId));
    }
  }

  async function onCreate() {
    if (!propertyId) return;
    setError(null);
    setCreating(true);
    try {
      const response = await propertyLiensApi.create({
        auction_property_id: propertyId,
        lien_type: createForm.lien_type?.trim() || null,
        amount: parseMoneyToNumber(createForm.amount),
        recorded_at: createForm.recorded_at || null,
        status: createForm.status || "UNKNOWN",
        source_url: createForm.source_url?.trim() || null,
        notes: createForm.notes?.trim() || null,
      });

      if (!response?.success) {
        throw new Error(response?.error || "Falha ao criar lien");
      }

      setCreateForm(EMPTY_LIEN_FORM);
      await refreshProperty();
    } catch (error) {
      setError(error?.message || "Erro ao criar lien");
    } finally {
      setCreating(false);
    }
  }

  async function onUpdate(lienId) {
    if (!lienId) return;
    setError(null);
    setSavingId(lienId);
    try {
      const payload = editMap[lienId] || {};
      const response = await propertyLiensApi.update(lienId, {
        lien_type: payload.lien_type?.trim() || null,
        amount: parseMoneyToNumber(payload.amount),
        recorded_at: payload.recorded_at || null,
        status: payload.status || "UNKNOWN",
        source_url: payload.source_url?.trim() || null,
        notes: payload.notes?.trim() || null,
      });

      if (!response?.success) {
        throw new Error(response?.error || "Falha ao salvar lien");
      }

      await refreshProperty();
    } catch (error) {
      setError(error?.message || "Erro ao salvar lien");
    } finally {
      setSavingId(null);
    }
  }

  async function onDelete(lienId) {
    if (!lienId) return;
    setError(null);
    setDeletingId(lienId);
    try {
      const response = await propertyLiensApi.remove(lienId);
      if (!response?.success) {
        throw new Error(response?.error || "Falha ao deletar lien");
      }

      await refreshProperty();
    } catch (error) {
      setError(error?.message || "Erro ao deletar lien");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        {publicRecordsLinks.length > 0
          ? publicRecordsLinks
              .filter((link) => !!link?.url)
              .map((link) => (
                <Button
                  key={link.id || link.url}
                  className="gap-2"
                  color="dark"
                  onClick={() =>
                    window.open(link.url, "_blank", "noopener,noreferrer")
                  }
                >
                  {link.label?.trim() || "Public Records"} <HiExternalLink />
                </Button>
              ))
          : publicRecordsLink?.url && (
              <Button
                className="gap-2"
                color="dark"
                onClick={() =>
                  window.open(
                    publicRecordsLink.url,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                {publicRecordsLink.label?.trim() || "Public Records"}{" "}
                <HiExternalLink />
              </Button>
            )}
      </div>

      <LiensSummaryCards summary={summary} toMoney={toMoney} />

      <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 space-y-3 bg-white dark:bg-gray-900">
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          Adicionar Lien
        </div>

        <LienFormFields
          form={createForm}
          onChange={(patch) =>
            setCreateForm((current) => ({ ...current, ...patch }))
          }
        />

        <div className="flex justify-end gap-2">
          <Button color="gray" onClick={onCreate} disabled={creating}>
            {creating ? "Adicionando..." : "Adicionar"}
          </Button>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      {liensFromProperty.length > 0 ? (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            Liens cadastrados
          </div>

          <div className="space-y-3">
            {liensFromProperty.map((lien) => (
              <LienEditorCard
                key={lien.id}
                deleting={deletingId === lien.id}
                form={editMap[lien.id] || EMPTY_LIEN_FORM}
                onChange={(patch) => setEdit(lien.id, patch)}
                onDelete={() => onDelete(lien.id)}
                onSave={() => onUpdate(lien.id)}
                saving={savingId === lien.id}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-600 dark:text-gray-300">
          Nenhum lien cadastrado ainda.
        </div>
      )}
    </div>
  );
}
